import { fetchViaProxy } from '@/actions/proxy-fetch';
import { authorizedServerFetch } from '@/actions/server.actions';
import { getUserCurrency } from '@/utils/user.helper';

const raiseDonationIntent = async (nonProfit, payload, post) => {
  const intentPayload = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    nonProfitId: nonProfit?.id,
    currency: 'USD',
    provider: 'paypal',
    origin: window.location.origin,
    originPage: window.location.pathname,
    amount: payload.amount,
    tipAmount: payload.tip,
    isDedicated: payload.doDedicate,
    isAnonymous: payload.isAnonymous,
    dedicateToName: payload.dedicatedToName,
    dedicateToEmail: payload.dedicatedToEmail,
    dedicateToMessage: payload.dedicatedToMessage,
    shareNameAndEmail: payload.shareNameAndEmail,
    isRecurring: false,
    gatewayData: {
      paymentMethod: 'DIRECT',
    },
    agreeTerms: payload.agreeTerms,
    postId: post ? post.id : null,
    referCompanyId: post ? post?.renderProps?.company?.id : null,
  };

  const formData = new FormData();
  formData.append('data', JSON.stringify(intentPayload));

  const { ok, data } = await fetchViaProxy(
    `/proxy/v1/users/me/donations/intent`,
    {
      method: 'POST',
      body: JSON.stringify(intentPayload),
    },
  );

  if (ok) {
    return data?.data;
  }

  return null;
};

const raiseDonationIntentPublic = async (nonProfit, payload, post) => {
  const intentPayload = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    nonProfitId: nonProfit?.id,
    currency: 'USD',
    provider: 'paypal',
    origin: window.location.origin,
    originPage: window.location.pathname,
    amount: payload.amount,
    tipAmount: payload.tip,
    isDedicated: payload.doDedicate,
    isAnonymous: payload.isAnonymous,
    dedicateToName: payload.dedicatedToName,
    dedicateToEmail: payload.dedicatedToEmail,
    dedicateToMessage: payload.dedicatedToMessage,
    shareNameAndEmail: payload.shareNameAndEmail,
    gatewayData: {
      paymentMethod: 'DIRECT',
    },
    agreeTerms: payload.agreeTerms,
    postId: post ? post.id : null,
    referCompanyId: post ? post?.renderProps?.company?.id : null,
  };

  const res = await fetch(
    `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/stripe/connect/donations/intent`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(intentPayload),
    },
  );

  if (res.ok) {
    const { data } = await res.json();
    return data;
  }

  return null;
};

const raiseRecurringDonationIntent = async (nonProfit, payload, post, user) => {
  const intentPayload = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    originPage: window.location.pathname,
    nonProfitId: nonProfit?.id,
    currency: getUserCurrency(user),
    provider: 'paypal',
    postId: post ? post.id : null,
    origin: window.location.origin,
    amount: payload.amount,
    tipAmount: payload.tip,
    isRecurring: true,
    isDedicated: payload.doDedicate,
    isAnonymous: payload.isAnonymous,
    dedicateToName: payload.dedicatedToName,
    dedicateToEmail: payload.dedicatedToEmail,
    dedicateToMessage: payload.dedicatedToMessage,
    gatewayData: {
      paymentMethod: 'BILLING_AGREEMENT',
    },
    shareNameAndEmail: payload.shareNameAndEmail,
    agreeTerms: payload.agreeTerms,
    referCompanyId: post ? post?.renderProps?.company?.id : null,
  };

  const formData = new FormData();
  formData.append('data', JSON.stringify(intentPayload));

  const { ok, data } = await fetchViaProxy(
    `/proxy/v1/users/me/donations/intent`,
    {
      method: 'POST',
      body: JSON.stringify(intentPayload),
    },
  );

  if (ok) {
    return data?.data;
  }

  return null;
};

const makeGiftOfGivingDonation = async (nonProfit, payload, post, user) => {
  const postPayload = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    nonProfitId: nonProfit?.id,
    currency: user?.giftOfGiving?.currency || getUserCurrency(user),
    postId: post ? post.id : null,
    amount: payload.amount,
    isAnonymous: payload.isAnonymous, // TODO: for sharing name and email
    isDedicated: payload.doDedicate,
    dedicateToName: payload.dedicatedToName,
    dedicateToEmail: payload.dedicatedToEmail,
    dedicateToMessage: payload.dedicatedToMessage,
    agreeTerms: payload.agreeTerms,
    referCompanyId: post ? post?.renderProps?.company?.id : null,
  };

  const formData = new FormData();
  formData.append('data', JSON.stringify(postPayload));

  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/users/me/gift-of-giving/donation`,
    {
      method: 'POST',
      body: JSON.stringify(postPayload),
    },
  );

  console.log('makeGiftOfGivingDonation', data);

  if (ok) {
    return data?.data;
  }

  return null;
};

const raiseStripeDonationIntent = async (
  nonProfit,
  payload,
  post,
  isRecurring,
) => {
  const intentPayload = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    nonProfitId: nonProfit?.id,
    currency: 'USD',
    provider: 'stripe',
    origin: window.location.origin,
    originPage: window.location.pathname,
    amount: payload.amount,
    tipAmount: (payload.tip || 0).toFixed(2),
    isDedicated: payload.doDedicate,
    isAnonymous: payload.isAnonymous,
    dedicateToName: payload.dedicatedToName,
    dedicateToEmail: payload.dedicatedToEmail,
    dedicateToMessage: payload.dedicatedToMessage,
    shareNameAndEmail: payload.shareNameAndEmail,
    // isRecurring: isRecurring,
    isRecurring: false,
    gatewayData: isRecurring
      ? { paymentMode: 'subscription', billingPeriod: payload.billingPeriod }
      : { paymentMode: 'payment', billingPeriod: payload.billingPeriod },
    agreeTerms: payload.agreeTerms,
    postId: post ? post.id : null,
    referCompanyId: post ? post?.renderProps?.company?.id : null,
    stripeAccountId: nonProfit.stripe_id,
    billingPeriod: payload.billingPeriod,
  };

  console.log('raiseStripeDonationIntent intentPayload', intentPayload);

  const formData = new FormData();
  formData.append('data', JSON.stringify(intentPayload));

  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/stripe/connect/donations/intent`,
    {
      method: 'POST',
      body: JSON.stringify(intentPayload),
    },
  );

  console.log('raiseStripeDonationIntentPublic data', data);

  if (ok) {
    return data?.data;
  }

  return null;
};

const raiseStripeDonationIntentPublic = async (nonProfit, payload, post) => {
  const intentPayload = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    nonProfitId: nonProfit?.id,
    currency: 'USD',
    provider: 'stripe',
    origin: window.location.origin,
    originPage: window.location.pathname,
    amount: payload.amount,
    tipAmount: payload.tip,
    isDedicated: payload.doDedicate,
    isAnonymous: payload.isAnonymous,
    dedicateToName: payload.dedicatedToName,
    dedicateToEmail: payload.dedicatedToEmail,
    dedicateToMessage: payload.dedicatedToMessage,
    shareNameAndEmail: payload.shareNameAndEmail,
    // gatewayData: {
    //   paymentMethod: 'DIRECT',
    // },
    gatewayData: null,
    agreeTerms: payload.agreeTerms,
    postId: post ? post.id : null,
    referCompanyId: post ? post?.renderProps?.company?.id : null,
    // TODO: check
    stripeAccountId: nonProfit.stripe_id,
  };

  console.log('raiseStripeDonationIntentPublic intentPayload', intentPayload);

  const res = await fetch(
    `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/stripe/connect/donations/intent`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(intentPayload),
    },
  );

  console.log('raiseStripeDonationIntentPublic res', res);

  if (res.ok) {
    const { data } = await res.json();
    return data;
  }

  return null;
};

export {
  makeGiftOfGivingDonation,
  raiseDonationIntent,
  raiseDonationIntentPublic,
  raiseRecurringDonationIntent,
  raiseStripeDonationIntent,
  raiseStripeDonationIntentPublic,
};
